import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { version } from "../../../../package.json";
import { isReallyOnline } from "../../../lib/isOnline";
import { FiX } from "react-icons/fi";

interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

interface NavigationItem {
  id: string;
  label: string;
  path: string;
  icon?: string;
  badge?: number | React.ReactNode;
  featureFlag?: string; // Single feature flag (for backward compatibility)
  featureFlags?: string[]; // Multiple feature flags with OR logic
}

const Drawer: React.FC<DrawerProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOnline, setIsOnline] = useState(false);
  const { failedCount } = useSelector(
    (state: RootState) => state.pendingUploadsQueue
  );
  const tenant = useSelector((state: RootState) => state.auth.tenant);
  const userName = useSelector((state: RootState) => state.auth.userName);
  const featureFlags = useSelector((state: RootState) => state.auth.featureFlags);

  useEffect(() => {
    const checkOnlineStatus = async () => {
      const isOnline = await isReallyOnline();
      setIsOnline(isOnline);
    };
    checkOnlineStatus();
  }, [isOpen]);

  // Helper function to check if navigation item should be shown
  const shouldShowNavigationItem = (item: NavigationItem): boolean => {
    // Show if no feature flag required
    if (!item.featureFlag && !item.featureFlags) return true;
    
    // Handle undefined featureFlags (before API call completes)
    if (!featureFlags) return false;
    
    // Support multiple flags with OR logic
    if (item.featureFlags && item.featureFlags.length > 0) {
      return item.featureFlags.some(flag => featureFlags[flag] || false);
    }
    
    // Single flag support (backward compatible)
    if (item.featureFlag) {
      return featureFlags[item.featureFlag] || false;
    }
    
    return false;
  };

  const allNavigationItems: NavigationItem[] = [
    {
      id: "dashboard",
      label: "Dashboard",
      path: "/dashboard",
    },
    {
      id: "work-orders",
      label: "Work Orders",
      path: "/work-orders",
    },
    {
      id: "inbound",
      label: "Inbound Items",
      path: "/inbound-items",
      featureFlag: "enableTenantInboundAccess",
    },
    {
      id: "pending-requests",
      label: "Pending Requests",
      path: "/pending-requests",
      badge:
        failedCount > 0 ? (
          <div className="text-xs font-medium pill-badge bg-red-500 text-white">
            {failedCount}
          </div>
        ) : null,
    },
    {
      id: "settings",
      label: "Settings",
      path: "/settings",
    },
  ];

  // Filter navigation items based on feature flags
  const navigationItems = allNavigationItems.filter(shouldShowNavigationItem);

  const handleNavigation = (path: string) => {
    navigate(path);
    onClose();
  };

  const isActiveRoute = (path: string) => {
    if (path === "/dashboard") {
      return location.pathname === "/dashboard";
    }
    if (path === "/work-orders") {
      return location.pathname === "/work-orders";
    }

    if (path === "/inbound-items") {
      return location.pathname === "/inbound-items";
    }
    if (path === "/settings") {
      return location.pathname === "/settings";
    }
    if (path === "/pending-requests") {
      return location.pathname === "/pending-requests";
    }

    return location.pathname === path;
  };

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={onClose}
        />
      )}

      {/* Drawer */}
      <div
        className={`fixed top-0 right-0 h-full w-80 bg-white shadow-xl transform transition-transform duration-300 ease-in-out z-50 ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="bg-primary text-white p-6">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold mb-0">
                  {tenant || "Company"}
                </h2>
                <div className="absolute px-2 text-white rounded-full flex items-center justify-center space-x-2 shadow-md p-1 text-xs font-bold z-50 right-4 top-1 bg-white bg-opacity-30">
                  <div
                    className={`w-2 h-2 rounded-full ${
                      !isOnline ? "bg-red-400" : "bg-green-400"
                    }`}
                  ></div>
                  <span className="text-xs font-medium">
                    {!isOnline ? "Offline" : "Online"}
                  </span>
                </div>
              </div>
              {userName && <p className="mb-1 text-xs">{userName}</p>}
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <FiX className="w-6 h-6 text-white" />
            </button>
          </div>
          {/* Company Info */}
          <div className="text-sm opacity-90"></div>
        </div>
        {/* Navigation Items */}
        <nav className="flex-1 overflow-y-auto">
          <div className="p-4">
            {navigationItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handleNavigation(item.path)}
                className={`w-full flex items-center justify-between p-4 mb-2 rounded-lg transition-all duration-200 ${
                  isActiveRoute(item.path)
                    ? "bg-primary text-white shadow-md"
                    : "text-gray-700 hover:bg-gray-100"
                }`}
              >
                <div className="flex items-center space-x-3">
                  {item.icon && (
                    <img
                      src={item.icon}
                      alt={item.label}
                      className={`w-5 h-5 ${
                        isActiveRoute(item.path)
                          ? "filter brightness-0 invert"
                          : ""
                      }`}
                    />
                  )}
                  <span className="font-medium">{item.label}</span>
                </div>

                {item.badge && (
                  <span
                    className={`px-2 py-1 text-xs rounded-full ${
                      isActiveRoute(item.path)
                        ? "bg-white text-primary"
                        : "bg-red-500 text-white"
                    }`}
                  >
                    {item.badge}
                  </span>
                )}
              </button>
            ))}
          </div>
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200">
          <div className="text-center text-xs text-gray-500">
            <p>Powered by InnovA</p>
            <p>Version: {version}</p>
          </div>
        </div>
      </div>
    </>
  );
};

export default Drawer;
